import { useState, useEffect, useRef, useCallback } from 'react';
import { dgiiApi } from '../../api/dgii';
import { useAuth } from '../../contexts/AuthContext';
import { MagnifyingGlassIcon, XMarkIcon, ChevronDownIcon, PlusIcon } from '@heroicons/react/24/outline';

export default function ActividadEconomicaSelector({ value, onChange, placeholder = 'Buscar actividad económica...', className = '', onAddNew, showGrupos = true }) {
	const { isAdmin } = useAuth();
	const [searchTerm, setSearchTerm] = useState('');
	const [actividades, setActividades] = useState([]);
	const [grupos, setGrupos] = useState([]);
	const [selectedGrupo, setSelectedGrupo] = useState('');
	const [isOpen, setIsOpen] = useState(false);
	const [loading, setLoading] = useState(false);
	const [selectedActividad, setSelectedActividad] = useState(null);
	const wrapperRef = useRef(null);
	const searchInputRef = useRef(null);
	const searchTimeoutRef = useRef(null);

	// Cargar grupos al montar
	useEffect(() => {
		cargarGrupos();
	}, []);

	// Cargar actividad seleccionada si hay value
	useEffect(() => {
		if (value) {
			// Si value es un objeto con código
			if (typeof value === 'object' && value.codigo) {
				setSelectedActividad(value);
				setSearchTerm(value.descripcion || '');
			} 
			// Si value es un string que contiene código y descripción (formato: "0001 - Descripción")
			else if (typeof value === 'string') {
				const match = value.match(/^(\d{4})\s*-\s*(.+)$/);
				if (match) {
					const codigo = match[1];
					const descripcion = match[2];
					cargarActividadPorCodigo(codigo);
				} 
				// Si es solo un código de 4 dígitos
				else if (/^\d{4}$/.test(value.trim())) {
					cargarActividadPorCodigo(value.trim());
				} 
				// Si es texto libre, mostrarlo pero no buscar
				else {
					setSearchTerm(value);
				}
			}
		} else {
			setSelectedActividad(null);
			setSearchTerm('');
		}
	}, [value]);

	// Cerrar dropdown al hacer click fuera
	useEffect(() => {
		function handleClickOutside(event) {
			if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
				setIsOpen(false);
			}
		}
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
			if (searchTimeoutRef.current) {
				clearTimeout(searchTimeoutRef.current);
			}
		};
	}, []);

	const cargarGrupos = async () => {
		try {
			const data = await dgiiApi.getGruposEconomicos();
			if (Array.isArray(data)) {
				setGrupos(data);
			} else {
				setGrupos([]);
			}
		} catch (err) {
			console.error('Error cargando grupos:', err);
			setGrupos([]);
		}
	};

	const cargarActividadPorCodigo = async (codigo) => {
		try {
			const data = await dgiiApi.getActividadesEconomicas(codigo);
			const actividadesArray = Array.isArray(data) ? data : [];
			if (actividadesArray.length > 0) {
				const actividad = actividadesArray[0];
				setSelectedActividad(actividad);
				setSearchTerm(actividad.descripcion || '');
			}
		} catch (err) {
			console.error('Error cargando actividad:', err);
		}
	};

	const buscarActividades = useCallback(async (term) => {
		if (!term || term.trim().length < 1) {
			setActividades([]);
			setIsOpen(false);
			return;
		}

		setLoading(true);
		setIsOpen(true);
		try {
			const data = await dgiiApi.getActividadesEconomicas(term.trim(), selectedGrupo);
			const actividadesArray = Array.isArray(data) ? data : [];
			setActividades(actividadesArray);
			// Si hay resultados, mantener el dropdown abierto
			if (actividadesArray.length > 0) {
				setIsOpen(true);
			}
		} catch (err) {
			console.error('Error buscando actividades:', err);
			console.error('Detalles del error:', err.response?.data || err.message);
			setActividades([]);
			setIsOpen(false);
		} finally {
			setLoading(false);
		}
	}, [selectedGrupo]);

	const handleSearchChange = (e) => {
		const term = e.target.value;
		setSearchTerm(term);
		setSelectedActividad(null);
		
		// Limpiar timeout anterior
		if (searchTimeoutRef.current) {
			clearTimeout(searchTimeoutRef.current);
		}
		
		// Si el término está vacío, limpiar resultados
		if (!term || term.trim().length === 0) {
			setActividades([]);
			setIsOpen(false);
			return;
		}
		
		// Debounce: esperar 300ms antes de buscar
		searchTimeoutRef.current = setTimeout(() => {
			buscarActividades(term);
		}, 300);
	};

	const handleSelectActividad = (actividad) => {
		setSelectedActividad(actividad);
		setSearchTerm(actividad.descripcion);
		setIsOpen(false);
		if (onChange) {
			onChange(actividad);
		}
	};

	const handleClear = () => {
		setSelectedActividad(null);
		setSearchTerm('');
		setActividades([]);
		setIsOpen(false);
		if (onChange) {
			onChange(null);
		}
		if (searchInputRef.current) {
			searchInputRef.current.focus();
		}
	};

	const handleGrupoChange = (e) => {
		const grupo = e.target.value;
		setSelectedGrupo(grupo);
		if (searchTerm && searchTerm.trim().length >= 1) {
			buscarActividades(searchTerm);
		}
	};

	return (
		<div ref={wrapperRef} className={`relative ${className}`}>
			{/* Selector de grupo (opcional) - Solo mostrar si showGrupos es true */}
			{showGrupos && grupos.length > 0 && (
				<div className="mb-2">
					<select
						value={selectedGrupo}
						onChange={handleGrupoChange}
						className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
					>
						<option value="">Todos los grupos</option>
						{grupos.map((grupo) => (
							<option key={grupo.literal} value={grupo.literal}>
								{grupo.literal} - {grupo.descripcion}
							</option>
						))}
					</select>
				</div>
			)}

			{/* Campo de búsqueda */}
			<div className="relative">
				<div className="relative">
					<MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
					<input
						ref={searchInputRef}
						type="text"
						value={searchTerm}
						onChange={handleSearchChange}
						onFocus={() => {
							if (actividades.length > 0 || searchTerm.trim().length >= 1) {
								setIsOpen(true);
								if (searchTerm.trim().length >= 1 && actividades.length === 0 && !loading) {
									buscarActividades(searchTerm);
								}
							}
						}}
						onKeyDown={(e) => {
							// Si presiona Escape, cerrar dropdown
							if (e.key === 'Escape') {
								setIsOpen(false);
							}
						}}
						placeholder={placeholder}
						className={`w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${className}`}
					/>
					{selectedActividad && (
						<button
							type="button"
							onClick={handleClear}
							className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
						>
							<XMarkIcon className="h-5 w-5" />
						</button>
					)}
					{!selectedActividad && (
						<ChevronDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
					)}
				</div>

				{/* Dropdown de resultados */}
				{isOpen && (
					<div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
						{loading ? (
							<div className="p-4 text-center text-gray-500">
								<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
								<p className="mt-2 text-sm">Buscando...</p>
							</div>
						) : actividades.length > 0 ? (
							<ul className="py-1">
								{actividades.map((actividad) => (
									<li
										key={actividad.id}
										onClick={() => handleSelectActividad(actividad)}
										className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
									>
										<div className="flex items-start justify-between">
											<div className="flex-1">
												<div className="flex items-center gap-2">
													<span className="font-semibold text-primary">{actividad.codigo}</span>
													<span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
														{actividad.literal_grupo}
													</span>
												</div>
												<p className="text-sm text-gray-700 mt-1">{actividad.descripcion}</p>
												<p className="text-xs text-gray-500 mt-1">{actividad.descripcion_grupo}</p>
											</div>
										</div>
									</li>
								))}
							</ul>
						) : searchTerm.trim().length > 0 ? (
							<div className="p-4 text-center">
								<p className="text-gray-500 text-sm mb-3">
									No se encontraron actividades para "{searchTerm}"
								</p>
							{isAdmin() && onAddNew && (
								<button
									type="button"
									onClick={() => {
										setIsOpen(false);
										onAddNew(searchTerm);
									}}
									className="flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium mx-auto"
								>
									<PlusIcon className="h-4 w-4" />
									Agregar nueva actividad
								</button>
							)}
							</div>
						) : (
							<div className="p-4 text-center text-gray-500 text-sm">
								Escribe al menos 1 carácter para buscar
							</div>
						)}
					</div>
				)}
			</div>

			{/* Mostrar actividad seleccionada */}
			{selectedActividad && (
				<div className="mt-2 p-2 bg-primary/10 border border-primary/20 rounded-lg">
					<div className="flex items-center justify-between">
						<div>
							<span className="text-sm font-semibold text-primary">{selectedActividad.codigo}</span>
							<span className="text-sm text-gray-600 ml-2">{selectedActividad.descripcion}</span>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

